<template>
  <div class="drip-table-container">
    <loader :height="300" v-if="loading" />
    <div
      class="table-display"
      @scroll="onScrollDisplay"
      v-else-if="invoices.length > 0"
    >
      <b-table
        sort-icon-left
        :items="invoices"
        :fields="renderTableFields"
        :sort-by.sync="sortByVal"
        :sort-desc.sync="sortDescVal"
        :no-sort-reset="true"
        class="drip-v2-table table-borderless mb-5 pb-3"
        tbody-tr-class="drip-tr-class"
        thead-class="drip-table-head"
      >
        <template v-slot:cell(action)="data">
          <download-dropdown
            :options="fetchDownloadLinks(data)"
            @fireEvent="dropdownEvent"
            :disabled="isNotAdvanced(data)"
          />
        </template>
        <template #cell(reference)="data">
          <div
            class="truncate_text"
            style="max-width: 96px"
            :title="data.item.reference"
            :data-invoice-id="data.item.id"
          >
            <router-link
              v-if="isDraft(data.item.stage_human)"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : { name: 'invoicesAdd', params: { id: data.item.id } }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              {{ data.item.reference }}
            </router-link>
            <router-link
              v-else-if="isDocsPending(data.item.stage_human)"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : {
                      name: 'invoicesAdd',
                      params: { id: data.item.id, value: 'upload-docs' }
                    }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              {{ data.item.reference }}
            </router-link>
            <router-link
              v-else-if="isReviewPending(data.item.stage_human)"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : {
                      name: 'invoicesAdd',
                      params: { id: data.item.id, value: 'review' }
                    }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              {{ data.item.reference }}
            </router-link>
            <div class="d-flex align-items-center" v-else>
              <router-link
                :to="
                  newInvoiceRedirect
                    ? { name: 'account' }
                    : { name: 'invoicesSingle', params: { id: data.item.id } }
                "
                v-track-click
                :id="`invoices-index-view-ref-${data.item.reference}`"
                class="truncate_text"
              >
                {{ data.item.reference }}
              </router-link>
            </div>
          </div>
        </template>
        <template #cell(importer)="data">
          <div class="truncate_text" style="max-width: 64px">
            {{ data.item.importer }}
          </div>
        </template>
        <template #cell(advanced_date)="data">
          <template v-if="data.item.advances.length > 0">
            <div style="max-width: 76px">
              {{ data.item.advances[0].disbursed | moment('MMM D, YYYY') }}
            </div>
          </template>
          <template v-else> - </template>
        </template>
        <template #cell(value)="data">
          <div style="max-width: 96px">
            {{ data.value | currency(data.item.currsym, null, 0) }}
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="text-right">
            <b-link
              class="view-invoices-link"
              @click="downloadInvoice(data.item.id)"
              v-if="parseFloat(data.item.advance_total) > 0"
              v-b-tooltip.hover.v-secondary
              :title="$t('invoices.index.downloadInvoice')"
            >
              <b-icon-download />
            </b-link>
            <router-link
              class="view-invoices-link"
              v-if="isDraft(data.item.stage_human)"
              v-b-tooltip.hover.v-secondary
              :title="$t('invoices.index.editDraft')"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : { name: 'invoicesAdd', params: { id: data.item.id } }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              <b-icon-chevron-right font-scale="0.75" />
            </router-link>
            <router-link
              class="view-invoices-link"
              v-else-if="isDocsPending(data.item.stage_human)"
              v-b-tooltip.hover.v-secondary
              :title="$t('invoices.index.editDraft')"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : {
                      name: 'invoicesAdd',
                      params: { id: data.item.id, value: 'upload-docs' }
                    }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              <b-icon-chevron-right font-scale="0.75" />
            </router-link>
            <router-link
              class="view-invoices-link"
              v-else-if="isReviewPending(data.item.stage_human)"
              v-b-tooltip.hover.v-secondary
              :title="$t('invoices.index.editDraft')"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : {
                      name: 'invoicesAdd',
                      params: { id: data.item.id, value: 'review' }
                    }
              "
              v-track-click
              :id="`invoices-index-edit-ref-${data.item.reference}`"
            >
              <b-icon-chevron-right font-scale="0.75" />
            </router-link>
            <router-link
              class="view-invoices-link"
              v-else
              v-b-tooltip.hover.v-secondary
              :title="$t('invoices.index.viewInvoice')"
              :to="
                newInvoiceRedirect
                  ? { name: 'account' }
                  : { name: 'invoicesSingle', params: { id: data.item.id } }
              "
              v-track-click
              :id="`invoices-index-view-ref-${data.item.reference}`"
            >
              <b-icon-chevron-right font-scale="0.75" />
            </router-link>
          </div>
        </template>
        <template #cell(repayment)="data">
          <div style="max-width: 60px">
            {{ data.value | moment('MMM D, YYYY') }}
          </div>
        </template>
        <template #cell(total_advanced)="data">
          <div style="max-width: 110px">
            <!-- We have 3 cases
          1. When We have advance.length === 0  -- show '-' (handled in v-else)
          2. When We have advance.length === 1 -- Show the transaction_amount
          3. When We have advance.length and drip_switch is enabled > 1 -- render redirection link to transactions page -->
            <template v-if="data.item.advances.length === 1">
              {{
                data.item.advances
                  .map((item) => parseFloat(item.value))
                  .reduce((prev, next) => prev + next)
                  | currency(data.item.currsym, null, 0)
              }}
              <div v-if="data.item.transaction_amount">
                You received
                <b>₹{{ commaSeparatedValue(data.item.transaction_amount) }}</b>
                at ₹{{ data.item.rate }} per {{ data.item.base_currency }}
              </div>
            </template>
            <template
              v-else-if="
                data.item.drip_switch_enabled && data.item.advances.length > 1
              "
            >
              <div>
                {{
                  data.item.advances
                    .map((item) => parseFloat(item.value))
                    .reduce((prev, next) => prev + next)
                    | currency(data.item.currsym, null, 0)
                }}
              </div>
              <router-link :to="{ name: 'transactions' }" v-track-click>
                Multiple Advances. Click for details.
              </router-link>
            </template>
            <template v-else> - </template>
          </div>
        </template>
        <template #cell(charges)="data">
          <div style="max-width: 48px; text-align: right">
            <template
              v-if="getInvoiceStageCategory(data.item.stage) === 'settled'"
            >
              {{
                (data.item.exporter_fees == 0
                  ? data.item.advice_exporter_fees
                  : data.item.exporter_fees)
                  | currency(data.item.currsym, null, 0)
              }}
              <span class="val-description d-block">
                {{ $t('invoiceTableItems.charged') }}
              </span>
            </template>
            <template v-else>
              {{
                data.item.expected_exporter_fees
                  | currency(data.item.currsym, null, 0)
              }}
              <span class="val-description d-block">
                {{ $t('invoiceTableItems.expected') }}
              </span>
            </template>
          </div>
        </template>
        <template #cell(stage_human)="data">
          <div
            class="invoice-status"
            :class="data.item.stage_human | invoiceStageClass"
          >
            <div
              class="invoices-stage-chip"
              :class="getInvoiceStageCategory(data.item.stage)"
            >
              {{
                $t(`invoiceTableItems.status['${data.item.stage_human}']`)
                  | invoiceStageDisplay
              }}
            </div>
          </div>
        </template>
        <template #cell(drip_switch_enabled)="data">
          <div class="currency-status">
            <div class="title alternate">
              {{
                data.item && data.item.drip_switch_toggle.enabled
                  ? 'Enabled'
                  : 'Disabled'
              }}
            </div>
            <div
              class="checkbox-container d-flex align-items-center justify-content-flex-end"
            >
              <toggle-switch
                v-if="
                  data.item.drip_switch_toggle &&
                  data.item.drip_switch_toggle.enabled
                "
                :value="data.item.drip_switch_enabled"
                :frozen="false"
                :tableCompatible="true"
                :onToggle="() => onSwitchChange(data.item)"
              />
              <div
                v-else
                v-b-tooltip.hover.window.v-secondary.o14.right
                :title="data.item.drip_switch_toggle.reason"
                class="d-flex align-items-center"
              >
                <toggle-switch
                  :value="data.item.drip_switch_enabled"
                  :frozen="true"
                  :tableCompatible="true"
                />
                <span class="pr-2">
                  <b-icon-info-circle
                    class="ml-2 color-wealthy-green-500"
                    scale="1.5"
                  />
                </span>
              </div>
            </div>
          </div>
        </template>
      </b-table>
      <loader :height="60" v-if="hasPages" />
      <enable-drip-switch-modal
        :refId="selectedInvoice.reference"
        :invoiceId="selectedInvoice.id"
        :invoiceLevel="true"
        :isEnableDripSwitchOpen="isEnableDripSwitchModalOpen"
        :dataToSend="getEnableDripSwitchApiData"
        :onClose="onModalClose"
        :onCancel="onModalCancel"
        :onSuccess="onSuccessEnabledSwitch"
      />
      <disable-drip-switch-sidebar
        :isDisableDripSwitchOpen="isDisableDripSwitchDrawerOpen"
        :dataToSend="getDisableDripSwitchApiData"
        :onCancel="onDrawerCancel"
        :onClose="onDrawerClose"
        :onSuccess="onSuccessDisableSwitch"
      />
      <disabled-message-modal
        :showDialog="showDisabledMessageModal"
        @closeDialog="onCloseDMMDialog"
      />
      <enabled-message-modal
        :showDialog="showEnabledMessageModal"
        @closeDialog="onCloseEMMDialog"
      />
    </div>
    <div class="invoices-empty-container pt-5" v-else>
      <h4 class="text-center">
        <!-- No invoices found -->
        {{ $t('invoices.index.notFound') }}
      </h4>
    </div>
  </div>
</template>
<script>
import Loader from '../../common/Loader'
import invoiceService from '@/services/invoice'
import downloadService from '@/services/download/'
import { commaSeparator } from '@/utils/common'
import ToggleSwitch from '@/components/common/Switch'
import EnableDripSwitchModal from '@/components/drip-switch/EnableDripSwitchModal'
import DisableDripSwitchSidebar from '@/components/drip-switch/DisableDripSwitchSidebar'
import DisabledMessageModal from '@/components/drip-switch/DisabledMessageModal'
import EnabledMessageModal from '@/components/drip-switch/EnabledMessageModal'
import DownloadDropdown from '@/components/common/DownloadDropdown'

export default {
  props: [
    'loading',
    'invoices',
    'pagination',
    'currentPage',
    'invoiceMeta',
    'sortBy',
    'sortDesc',
    'monitoringData'
  ],
  components: {
    Loader,
    ToggleSwitch,
    EnableDripSwitchModal,
    DisableDripSwitchSidebar,
    DisabledMessageModal,
    EnabledMessageModal,
    DownloadDropdown
  },
  data() {
    return {
      sortByVal: '',
      sortDescVal: true,
      invoiceTableFields: [
        {
          key: 'reference',
          label: 'Invoice #',
          class: 'reference-cell pl-1 pl-md-3',
          sortable: true
        },
        {
          key: 'importer',
          label: 'Buyer',
          class: 'buyer-cell',
          sortable: true
        },
        {
          key: 'value',
          label: 'Net Invoice Value',
          sortable: true
        },
        {
          key: 'advanced_date',
          label: 'Advanced Date'
          // sortable: true
        },
        {
          key: 'total_advanced',
          label: 'Advance Amount'
          // sortable: true
        },
        {
          key: 'charges',
          label: 'Charges'
          // sortable: true
        },
        {
          key: 'repayment',
          label: 'Due Date'
          // sortable: true
        },
        {
          key: 'stage_human',
          label: 'Status',
          class: 'status-cell'
          // sortable: true
        },
        {
          key: 'actions',
          label: 'Actions'
          // sortable: true
        }
      ],
      isDisableDripSwitchDrawerOpen: false,
      isEnableDripSwitchModalOpen: false,
      selectedInvoice: {},
      showDisabledMessageModal: false,
      showEnabledMessageModal: false
    }
  },
  watch: {
    sortDescVal() {
      if (this.sortDescVal) {
        this.$mpTrack(`Invoice list sortDescVal ${this.sortDescVal}`)
        this.$emit('update:sortDesc', this.sortDescVal)
      }
    },
    sortByVal() {
      if (this.sortByVal) {
        this.$mpTrack(`Invoice list sortByVal ${this.sortByVal}`)
        this.$emit('update:sortBy', this.sortByVal)
      }
    }
  },
  created() {
    this.sortByVal = this.sortBy
    this.sortDescVal = this.sortDesc
  },
  methods: {
    fetchDownloadLinks(data) {
      return [
        {
          text: this.$t('common.invoiceSwift'),
          data: {
            id: data.item.id,
            type: 'swift',
            invType: ['paid', 'closed'].includes(data.item.user_stage)
              ? 'all'
              : 'advances',
            ref: data.item.reference,
            date:
              data.item.swiftType === 'advance'
                ? data.item.disbursed_date
                : data.item.settlement_date,
            swiftType: data.item.swiftType
          },
          disabled: !data.item.swiftAvail
        },
        {
          text: this.$t('common.invoiceNoc'),
          data: {
            id: data.item.id,
            type: 'noc',
            invType: ['paid', 'closed'].includes(data.item.user_stage)
              ? 'all'
              : 'advances',
            ref: data.item.reference,
            date:
              data.item.swiftType === 'advance'
                ? data.item.disbursed_date
                : data.item.settlement_date,
            swiftType: data.item.swiftType
          },
          disabled: !data.item.nocAvail
        },
        {
          text: this.$t('common.invoiceAdvice'),
          data: {
            id: data.item.id,
            type: 'advice'
          },
          disabled: !parseFloat(data.item.advance_total) > 0
        }
      ]
    },
    downloadInvoiceAdvice(invId) {
      this.$store.dispatch('setDownloadNotification', {
        fileDownloadNotification: true
      })
      return invoiceService
        .downloadInvoice(invId)
        .then((res) => {
          downloadService.downloadPDF(res.data, `invoices-${invId}-advice.pdf`)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
        .catch((e) => {
          console.log(e)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
    },
    async downloadInvoiceSwiftCopy(data) {
      if (data.id) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })

        let res = await invoiceService
          .downloadSwiftCopy('invoices', data.id, data.invType)
          .catch(console.log)
        if (res.status === 204) {
          this.$notify({
            title: 'File not found!',
            type: 'error'
          })
        }
        const isPDF = res.data.type === 'application/pdf'
        let filename = downloadService.createFilename(
          isPDF,
          data.swiftType,
          data.ref,
          data.date
        )
        downloadService.downloadFile(res.data, filename)
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    async downloadNocDocument(data) {
      if (data.id) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })

        let res = await invoiceService
          .downloadNocCopy('invoices', data.id, data.invType)
          .catch(console.log)
        if (res.status === 204) {
          this.$notify({
            title: 'File not found!',
            type: 'error'
          })
        }
        const isPDF = res.data.type === 'application/pdf'
        let filename = downloadService.createFilename(
          isPDF,
          data.swiftType,
          data.ref,
          data.date
        )
        downloadService.downloadFile(res.data, filename)
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    dropdownEvent(links) {
      if (links.data && links.data.type === 'advice') {
        this.$mpTrack('Advance invoices - Download Invoice advice')
        this.downloadInvoiceAdvice(links.data.id)
      } else if (links.data && links.data.type === 'noc') {
        this.$mpTrack('Advance invoices - Download Noc Document')
        this.downloadNocDocument(links.data)
      } else {
        this.$mpTrack('Advance invoices - Download Swift copy')
        this.downloadInvoiceSwiftCopy(links.data)
      }
    },
    isNotAdvanced(data) {
      return [
        'draft',
        'new',
        'first_invoice',
        'info_pending',
        'pending',
        'uploaded',
        'generated',
        'data_entry',
        'processed',
        'data_entry_verification',
        'buyer_esign_pending',
        'pd_received_pending',
        'bank_cover_pending',
        'exp_checklist_pending',
        'cma_bl_pending',
        'pdc_pending',
        'buyer_not_approved',
        'submitted',
        'processing',
        'deposit_pending',
        'exhibit_a_verification',
        'verify_bank_details',
        'pending_advance',
        'verified',
        'deposited'
      ].includes(data.item.user_stage)
    },
    onSwitchChange(item) {
      if (item) {
        if (this.$route.name === 'currencySettings') {
          this.$mpTrack(
            `Clicks on ${
              item.drip_switch_enabled ? 'enable' : 'disable'
            } INR for a specific invoice on the currency settings page`,
            {
              currentValue: item.drip_switch_enabled,
              invoiceId: item.id
            }
          )
        }
        if (this.$route.name === 'invoices') {
          this.$mpTrack(
            `Clicks on ${
              item.drip_switch_enabled ? 'enable' : 'disable'
            } INR for a specific invoice on the Invoices page`,
            {
              currentValue: item.drip_switch_enabled,
              invoiceId: item.id
            }
          )
        }
        if (item.drip_switch_enabled) {
          this.selectedInvoice = item
          this.isDisableDripSwitchDrawerOpen = true
        } else {
          this.selectedInvoice = item
          this.isEnableDripSwitchModalOpen = true
        }
      }
    },
    getInvoiceStageCategory(invoiceStage) {
      if (!this.invoiceMeta) {
        return ''
      }
      let stage = ''
      Object.keys(this.invoiceMeta.groups).forEach((category) => {
        if (this.invoiceMeta.groups[category].indexOf(invoiceStage) > -1) {
          stage = category
        }
      })
      return stage
    },
    onScrollDisplay({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight - 1) {
        if (this.hasPages) {
          this.$emit('scrollBottom')
        }
      }
    },
    bottomVisible: () => {
      const scrollY = window.scrollY
      const visible = document.documentElement.clientHeight
      const pageHeight = document.documentElement.scrollHeight
      const bottomOfPage = visible + scrollY >= pageHeight
      return bottomOfPage || pageHeight < visible
    },
    downloadInvoice(invoiceId) {
      return invoiceService
        .downloadInvoice(invoiceId)
        .then((res) => {
          downloadService.downloadPDF(
            res.data,
            `invoices-${invoiceId}-advice.pdf`
          )
        })
        .catch((e) => {
          console.log(e)
        })
    },
    isDraft(stage) {
      return [
        this.$t(`invoiceTableItems.status.Draft`),
        this.$t(`invoiceTableItems.status['Basic Info Pending']`)
      ].includes(stage)
    },
    isDocsPending(stage) {
      return stage === this.$t(`invoiceTableItems.status['Docs Pending']`)
    },
    isReviewPending(stage) {
      return stage === this.$store.state.constants.invoiceStage.review
    },
    commaSeparatedValue(value) {
      return commaSeparator(value)
    },
    onModalClose() {
      this.isEnableDripSwitchModalOpen = false
    },
    onModalCancel() {
      this.isEnableDripSwitchModalOpen = false
    },
    onDrawerClose() {
      this.isDisableDripSwitchDrawerOpen = false
    },
    onDrawerCancel() {
      this.isDisableDripSwitchDrawerOpen = false
    },
    onSuccessDisableSwitch() {
      this.$emit('refreshInvoices')
      this.showDisabledMessageModal = true
    },
    onSuccessEnabledSwitch() {
      this.$emit('refreshInvoices')
      this.showEnabledMessageModal = true
    },
    onCloseEMMDialog() {
      this.showEnabledMessageModal = false
    },
    onCloseDMMDialog() {
      this.showDisabledMessageModal = false
    }
  },
  computed: {
    isIndia() {
      return this.$store.state.account.country === 'India'
    },
    renderTableFields() {
      if (this.isIndia) {
        return this.$t('invoiceTableFields')
      }
      return this.$t('invoiceTableFields').filter(
        (item) => item.key !== 'drip_switch_enabled'
      )
    },
    getDisableDripSwitchApiData() {
      return {
        invoice_id: this.selectedInvoice.id,
        user_id: this.$store.state.account.accountId,
        enabled: false
      }
    },
    getEnableDripSwitchApiData() {
      return {
        invoice_id: this.selectedInvoice.id,
        user_id: this.$store.state.account.accountId,
        enabled: true
      }
    },
    hasPages() {
      return this.currentPage < this.pagination.total_pages
    },
    newInvoiceRedirect() {
      if (
        !!this.monitoringData &&
        !!this.monitoringData.mail_date &&
        this.$moment(this.monitoringData.revoke_date).isValid() &&
        this.$moment(this.monitoringData.mail_date).isValid() &&
        (!this.monitoringData.financials.active ||
          !this.monitoringData.gst.active)
      ) {
        const mailDate = new Date(this.monitoringData.mail_date) // Convert to Date object
        const currentDate = new Date() // Get today's date

        // Calculate the date 45 days after the mail_date
        const days60AfterMailDate = new Date(mailDate)
        days60AfterMailDate.setDate(mailDate.getDate() + 60)

        return currentDate >= new Date(this.monitoringData.revoke_date)
      }
      return false
    }
  }
}
</script>
<style lang="scss">
.tooltip-inner {
  color: $black;
  background-color: $neutral-gray-100;
}
.arrow::before {
  border-right-color: $neutral-gray-100;
}
</style>
